
import { jsPDF } from "jspdf";
import "jspdf-autotable";

const STYLES = {
    COLORS: {
        HEADER_BG: [245, 245, 245],
        HEADER_TEXT: [0, 0, 0],   // Black text for highlights
        BORDER: [200, 200, 200],
        SPEKE_ORANGE: [255, 165, 0],
    },
    FONTS: {
        TITLE: { fontSize: 16, fontStyle: "bold" },
        DAY_LABEL: { fontSize: 12, fontStyle: "bold", textColor: [0, 102, 0] },
        BODY: { fontSize: 10, fontStyle: "normal" },
    }
};



const reportHelper = {
    methods: {
        async imageBase64(url) {
            try {
                const response = await fetch(url);
                if (!response.ok) return null;
                const blob = await response.blob();
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            } catch {
                return null;
            }
        },
        getDateNow() {
            return new Date().toLocaleString();
        },

        getDataProperty(obj, path, defaultValue = null) {
            return path.split('.').reduce((acc, part) => acc?.[part], obj) ?? defaultValue;
        },

        createHeader(content, customFill = STYLES.COLORS.HEADER_BG) {
            return {
                content,
                styles: {
                    fillColor: customFill,
                    lineWidth: 0.1,
                    lineColor: STYLES.COLORS.BORDER,
                    textColor: STYLES.COLORS.HEADER_TEXT,
                    fontStyle: 'bold'
                },
            };
        },
        /**
         * Generate Tour Itinerary PDF Report (Image Left, Text Right)
         * @param {Array} tourItineraries - Daily activities and metadata
         * @param {Array} costBreakdown - Price quotes/pax breakdown
         * @param {Array} optionalCosts - Optional activities/charters
         * @param {Object} includesExcludes - { included: [], excluded: [] }
         * @param {String} logoImage - Base64 PNG logo
         * @param {String} sceneryImage - Base64 JPEG scenery image
         */

        async _generateTourItinerary(tourItineraries, costBreakdown, optionalCosts, includesExcludes, logoImage, sceneryImage) {
            try {
                // Initialize doc
                const doc = new jsPDF("portrait", "pt", "a4");
                const pageWidth = doc.internal.pageSize.width;
                const centerX = pageWidth / 2;
                let finalY = 135;

                // Shared Style for Section Headers
                const sectionHeaderStyle = {
                    halign: 'left',
                    fontStyle: 'bold',
                    fontSize: 10,
                    textColor: [0, 0, 0],
                };

                // === PAGE 1: HEADER & BANNER ===
                this.addReportHeader(doc, logoImage);

                // 1. Top Banner Image
                if (sceneryImage) {
                    // Spans the page width with margins: x=40, y=110, width=pageWidth-80, height=220
                    doc.addImage(sceneryImage, 'JPEG', 40, 110, pageWidth - 80, 220);
                    finalY = 360; // Start below the image with a gap
                } else {
                    finalY = 135;
                }

                if (tourItineraries && tourItineraries.length > 0) {
                    // --- MAIN TOUR TITLE ---
                    const mainTourTitle = tourItineraries[0].tour?.tour_title
                        ? tourItineraries[0].tour.tour_title.toUpperCase()
                        : 'TOUR ITINERARY';

                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: mainTourTitle,
                            styles: {
                                halign: 'center',
                                fontStyle: 'bold',
                                fontSize: 10,
                                textColor: [0, 0, 0],
                            },
                        }]],
                        margin: { top: 10, bottom: 0 },
                    });

                    finalY += 30; // Gap below the main title

                    // === ITINERARY OVERVIEW SECTION ===
                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: 'ITINERARY OVERVIEW',
                            styles: sectionHeaderStyle,
                        }]],
                        margin: { left: 40, right: 40, top: 10, bottom: 0 },
                    });

                    finalY += 30;

                    // Generate the Overview Grid
                    const overviewData = tourItineraries.map((item, index) => {
                        const dayNum = item.day_number || index + 1;
                        const destination = item.park?.name || item.destination || '';
                        const activity = item.itinerary_title || item.item?.title || 'ACTIVITY';
                        const accommodation = item.lodge?.name || '';
                        const mealPlan = item.tour_lodge?.meal?.name || item.meal_plan_id || '';

                        return [`${dayNum}`, destination, activity.toUpperCase(), accommodation, mealPlan];
                    });

                    doc.autoTable({
                        startY: finalY,
                        head: [['Day', 'Destination', 'Activity', 'Accommodation', 'Meal Plan']],
                        body: overviewData,
                        theme: 'grid',
                        headStyles: {
                            fillColor: STYLES.COLORS.HEADER_BG,
                            textColor: STYLES.COLORS.HEADER_TEXT,
                            fontStyle: 'bold',
                            lineColor: STYLES.COLORS.BORDER,
                            lineWidth: 0.5
                        },
                        styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                        margin: { left: 40, right: 40 },
                    });

                    // === PAGE BREAK TO DETAILED ITINERARY ===
                    doc.addPage();
                    this.addReportHeader(doc, logoImage);
                    finalY = 110;

                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: 'DETAILED ITINERARY',
                            styles: sectionHeaderStyle,
                        }]],
                        margin: { left: 40, right: 40, top: 10, bottom: 0 },
                    });

                    finalY = doc.lastAutoTable.finalY + 30;

                    // Loop through daily details
                    tourItineraries.forEach((item, index) => {
                        const dayNum = item.day_number || index + 1;
                        const titleText = item.itinerary_title || item.item?.title || 'ACTIVITY';
                        const dayTitle = `Day ${dayNum} | ${titleText.toUpperCase()}`;
                        const descriptionText = item.additional_information || item.item?.description || '';

                        // Activity Title & Description Table
                        doc.autoTable({
                            startY: finalY,
                            margin: { left: 40, right: 40 },
                            body: [
                                [{
                                    content: dayTitle,
                                    styles: STYLES.FONTS.DAY_LABEL // Green, Bold Day Label
                                }],
                                [{ content: descriptionText }]
                            ],
                            theme: 'plain',
                            styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0] }
                        });

                        // Metadata Table (Accommodation, Meals, Transit)
                        const metaDataRows = [];
                        if (item.lodge?.name) metaDataRows.push(['Accommodation:', item.lodge.name]);
                        if (item.tour_lodge?.meal?.name) metaDataRows.push(['Meal Plan:', item.tour_lodge.meal.name]);
                        if (item.transit_time) metaDataRows.push(['Transit Time:', `${item.transit_time} ${item.transit_unit || ''}`]);
                        if (item.flight_time) metaDataRows.push(['Flight Time:', `${item.flight_time} Mins`]);

                        if (metaDataRows.length > 0) {
                            doc.autoTable({
                                startY: doc.lastAutoTable.finalY + 2,
                                margin: { left: 40, right: 40 },
                                body: metaDataRows,
                                theme: 'plain',
                                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 100 } },
                                styles: { fontSize: 9, cellPadding: 1, textColor: [0, 0, 0] }
                            });
                        }

                        finalY = doc.lastAutoTable.finalY + 30;

                        // Handle internal page overflow
                        if (finalY > 750 && index < tourItineraries.length - 1) {
                            doc.addPage();
                            this.addReportHeader(doc, logoImage);
                            finalY = 120;
                        }
                    });
                } else {
                    // Fallback if no itineraries exist
                    doc.autoTable({
                        startY: finalY,
                        body: [['No itinerary activities available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: { left: 40, right: 40 },
                    });
                }

                // === PAGE BREAK TO COSTS ===
                doc.addPage();
                this.addReportHeader(doc, logoImage);

                // --- TOUR QUOTE SECTION ---
                doc.autoTable({
                    startY: 110,
                    body: [[{
                        content: 'TOUR QUOTE',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: { left: 40, right: 40, top: 10, bottom: 0 },
                });

                if (costBreakdown && costBreakdown.length > 0) {
                    const costRows = [];
                    costBreakdown.forEach((item) => {
                        const pax_count = parseInt(item.pax) || 0;
                        const total_amount = parseFloat(item.first_total || 0) + parseFloat(item.second_total || 0);
                        const formatted_total = total_amount.toLocaleString(undefined, {
                            minimumFractionDigits: 2, maximumFractionDigits: 2
                        });
                        costRows.push([`${pax_count} Pax: USD ${formatted_total} + USD 1,500 Gorilla Permit Per Person`]);
                    });

                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: costRows,
                        theme: 'plain',
                        styles: { fontSize: 9, fontStyle: "normal" },
                        margin: { left: 40, right: 40 },
                        columnStyles: { 0: { cellWidth: 280 } }
                    });
                } else {
                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: [['No cost breakdown available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: { left: 40, right: 40 },
                    });
                }

                // --- OPTIONAL COSTS SECTION ---
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 20,
                    body: [[{
                        content: 'OPTIONAL COSTS:',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: { left: 40, right: 40, top: 10, bottom: 0 },
                });

                if (optionalCosts && optionalCosts.length > 0) {
                    const optionRows = [];
                    optionalCosts.forEach((item) => {
                        optionRows.push([`Optional: ${item.cost_detail} at ${item.unit_cost} Per Person/Per Day`]);
                    });

                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: optionRows,
                        theme: 'plain',
                        styles: { fontSize: 9, fontStyle: "normal" },
                        margin: { left: 40, right: 40 },
                        columnStyles: { 0: { cellWidth: 280 } }
                    });
                } else {
                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: [['No optional costs available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: { left: 40, right: 40 },
                    });
                }

                // --- SERVICES INFORMATION SECTION ---
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 20,
                    body: [[{
                        content: 'SERVICE INFORMATION',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: { left: 40, right: 40, top: 10, bottom: 0 },
                });

                // Included Services
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 25,
                    body: [[{
                        content: 'Included Services:',
                        styles: sectionHeaderStyle,
                    }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 10, bottom: 0 },
                });

                const included = this.getDataProperty(includesExcludes, 'included', []);
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 10,
                    body: included.length > 0 ? included.map(i => [i]) : [['']],
                    theme: 'plain',
                    margin: { left: 40, right: 40 },
                    styles: { fontSize: 9 }
                });

                // Excluded Services
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 25,
                    body: [[{
                        content: 'Excluded Services:',
                        styles: sectionHeaderStyle,
                    }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 10, bottom: 0 },
                });

                const excluded = this.getDataProperty(includesExcludes, 'excluded', []);
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 10,
                    body: excluded.length > 0 ? excluded.map(e => [e]) : [['']],
                    theme: 'plain',
                    margin: { left: 40, right: 40 },
                    styles: { fontSize: 9 }
                });

                // === GLOBAL PAGE FOOTER ===
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);

                    doc.setDrawColor(180, 180, 180);
                    doc.setLineWidth(0.5);
                    doc.line(40, 775, pageWidth - 40, 775);

                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(0, 0, 0);
                    doc.text('Plot 1459 Block 216, Agape Close,', 40, 789);
                    doc.text('Off Ntinda Kiwatule Road', 40, 799);
                    doc.text('Kampala-Uganda', 40, 809);

                    doc.text('Tel: +256-782-207009', 212, 789);
                    doc.text('Tel: +256-393-294531', 212, 799);
                    doc.text('info@spekeugandaholidays.com', 212, 809);

                    doc.text('Speke Uganda Holidays', 384, 789);
                    doc.text('www.spekeugandaholidays.com', 384, 799);

                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Tour Itinerary Generated: ${this.getDateNow()}`, 40, 825);
                    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, 825, { align: 'right' });
                }

                return doc.output("bloburl");

            } catch (error) {
                console.error("PDF Generation Error:", error);
                throw error;
            }
        },

        async generateTourItinerary(tourItineraries, costBreakdown, optionalCosts, includesExcludes, logoImage, sceneryImage) {
            try {
                // Initialize doc
                const doc = new jsPDF("portrait", "pt", "a4");
                const pageWidth = doc.internal.pageSize.width;
                const centerX = pageWidth / 2;
                let finalY = 135;

                // Shared Style for Section Headers
                const sectionHeaderStyle = {
                    halign: 'left',
                    fontStyle: 'bold',
                    fontSize: 10,
                    textColor: [0, 0, 0],
                };

                // NEW: Shared Page Margins for autoTable. 
                // bottom: 80 protects the footer. top: 120 protects the header on auto-generated pages.
                const tableMargin = { left: 40, right: 40, top: 120, bottom: 80 };

                // === PAGE 1: HEADER & BANNER ===
                this.addReportHeader(doc, logoImage);

                // 1. Top Banner Image
                if (sceneryImage) {
                    // Spans the page width with margins: x=40, y=110, width=pageWidth-80, height=220
                    doc.addImage(sceneryImage, 'JPEG', 40, 110, pageWidth - 80, 220);
                    finalY = 360; // Start below the image with a gap
                } else {
                    finalY = 135;
                }

                if (tourItineraries && tourItineraries.length > 0) {
                    // --- MAIN TOUR TITLE ---
                    const mainTourTitle = tourItineraries[0].tour?.tour_title
                        ? tourItineraries[0].tour.tour_title.toUpperCase()
                        : 'TOUR ITINERARY';

                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: mainTourTitle,
                            styles: {
                                halign: 'center',
                                fontStyle: 'bold',
                                fontSize: 10,
                                textColor: [0, 0, 0],
                            },
                        }]],
                        margin: tableMargin,
                    });

                    finalY = doc.lastAutoTable.finalY + 30; // Gap below the main title

                    // === ITINERARY OVERVIEW SECTION ===
                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: 'ITINERARY OVERVIEW',
                            styles: sectionHeaderStyle,
                        }]],
                        margin: tableMargin,
                    });

                    finalY = doc.lastAutoTable.finalY + 10;

                    // Generate the Overview Grid
                    const overviewData = tourItineraries.map((item, index) => {
                        const dayNum = item.day_number || index + 1;
                        const destination = item.park?.name || item.destination || '';
                        const activity = item.itinerary_title || item.item?.title || 'ACTIVITY';
                        const accommodation = item.lodge?.name || '';
                        const mealPlan = item.tour_lodge?.meal?.name || item.meal_plan_id || '';

                        return [`${dayNum}`, destination, activity.toUpperCase(), accommodation, mealPlan];
                    });

                    doc.autoTable({
                        startY: finalY,
                        head: [['Day', 'Destination', 'Activity', 'Accommodation', 'Meal Plan']],
                        body: overviewData,
                        theme: 'grid',
                        headStyles: {
                            fillColor: STYLES.COLORS.HEADER_BG,
                            textColor: STYLES.COLORS.HEADER_TEXT,
                            fontStyle: 'bold',
                            lineColor: STYLES.COLORS.BORDER,
                            lineWidth: 0.5
                        },
                        styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                        margin: tableMargin,
                    });

                    // === PAGE BREAK TO DETAILED ITINERARY ===
                    doc.addPage();
                    this.addReportHeader(doc, logoImage);
                    finalY = 110;

                    doc.autoTable({
                        startY: finalY,
                        body: [[{
                            content: 'DETAILED ITINERARY',
                            styles: sectionHeaderStyle,
                        }]],
                        margin: tableMargin,
                    });

                    finalY = doc.lastAutoTable.finalY + 30;

                    // Loop through daily details
                    tourItineraries.forEach((item, index) => {
                        const dayNum = item.day_number || index + 1;
                        const titleText = item.itinerary_title || item.item?.title || 'ACTIVITY';
                        const dayTitle = `Day ${dayNum} | ${titleText.toUpperCase()}`;
                        const descriptionText = item.additional_information || item.item?.description || '';

                        // Activity Title & Description Table
                        doc.autoTable({
                            startY: finalY,
                            margin: tableMargin,
                            body: [
                                [{
                                    content: dayTitle,
                                    styles: STYLES.FONTS.DAY_LABEL // Green, Bold Day Label
                                }],
                                [{ content: descriptionText }]
                            ],
                            theme: 'plain',
                            styles: { fontSize: 10, cellPadding: 2, textColor: [0, 0, 0] }
                        });

                        // Metadata Table (Accommodation, Meals, Transit)
                        const metaDataRows = [];
                        if (item.lodge?.name) metaDataRows.push(['Accommodation:', item.lodge.name]);
                        if (item.tour_lodge?.meal?.name) metaDataRows.push(['Meal Plan:', item.tour_lodge.meal.name]);
                        if (item.transit_time) metaDataRows.push(['Transit Time:', `${item.transit_time} ${item.transit_unit || ''}`]);
                        if (item.flight_time) metaDataRows.push(['Flight Time:', `${item.flight_time} Mins`]);

                        if (metaDataRows.length > 0) {
                            doc.autoTable({
                                startY: doc.lastAutoTable.finalY + 2,
                                margin: tableMargin,
                                body: metaDataRows,
                                theme: 'plain',
                                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 100 } },
                                styles: { fontSize: 9, cellPadding: 1, textColor: [0, 0, 0] }
                            });
                        }

                        finalY = doc.lastAutoTable.finalY + 30;

                        // Handle internal page overflow manually (Adjusted check to 730 due to new 80pt margin)
                        if (finalY > 730 && index < tourItineraries.length - 1) {
                            doc.addPage();
                            this.addReportHeader(doc, logoImage);
                            finalY = 120;
                        }
                    });
                } else {
                    // Fallback if no itineraries exist
                    doc.autoTable({
                        startY: finalY,
                        body: [['No itinerary activities available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: tableMargin,
                    });
                }

                // === PAGE BREAK TO COSTS ===
                doc.addPage();
                this.addReportHeader(doc, logoImage);

                // --- TOUR QUOTE SECTION ---
                doc.autoTable({
                    startY: 110,
                    body: [[{
                        content: 'TOUR QUOTE',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: tableMargin,
                });

                if (costBreakdown && costBreakdown.length > 0) {
                    const costRows = [];
                    costBreakdown.forEach((item) => {
                        const pax_count = parseInt(item.pax) || 0;
                        const total_amount = parseFloat(item.first_total || 0) + parseFloat(item.second_total || 0);
                        const formatted_total = total_amount.toLocaleString(undefined, {
                            minimumFractionDigits: 2, maximumFractionDigits: 2
                        });
                        costRows.push([`${pax_count} Pax: USD ${formatted_total} + USD 1,500 Gorilla Permit Per Person`]);
                    });

                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: costRows,
                        theme: 'plain',
                        styles: { fontSize: 9, fontStyle: "normal" },
                        margin: tableMargin,
                        columnStyles: { 0: { cellWidth: 280 } }
                    });
                } else {
                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: [['No cost breakdown available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: tableMargin,
                    });
                }

                // --- OPTIONAL COSTS SECTION ---
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 20,
                    body: [[{
                        content: 'OPTIONAL COSTS:',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: tableMargin,
                });

                if (optionalCosts && optionalCosts.length > 0) {
                    const optionRows = [];
                    optionalCosts.forEach((item) => {
                        optionRows.push([`Optional: ${item.cost_detail} at ${item.unit_cost} Per Person/Per Day`]);
                    });

                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: optionRows,
                        theme: 'plain',
                        styles: { fontSize: 9, fontStyle: "normal" },
                        margin: tableMargin,
                        columnStyles: { 0: { cellWidth: 280 } }
                    });
                } else {
                    doc.autoTable({
                        startY: doc.lastAutoTable.finalY + 5,
                        body: [['No optional costs available.']],
                        theme: 'plain',
                        styles: { fontSize: 10 },
                        margin: tableMargin,
                    });
                }

                // --- SERVICES INFORMATION SECTION ---
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 20,
                    body: [[{
                        content: 'SERVICE INFORMATION',
                        styles: sectionHeaderStyle,
                    }]],
                    margin: tableMargin,
                });

                // Included Services
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 15,
                    body: [[{
                        content: 'Included Services:',
                        styles: sectionHeaderStyle,
                    }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                const included = this.getDataProperty(includesExcludes, 'included', []);
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 5,
                    body: included.length > 0 ? included.map(i => [i]) : [['']],
                    theme: 'plain',
                    margin: tableMargin,
                    styles: { fontSize: 9 }
                });

                // Excluded Services
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 15,
                    body: [[{
                        content: 'Excluded Services:',
                        styles: sectionHeaderStyle,
                    }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                const excluded = this.getDataProperty(includesExcludes, 'excluded', []);
                doc.autoTable({
                    startY: doc.lastAutoTable.finalY + 5,
                    body: excluded.length > 0 ? excluded.map(e => [e]) : [['']],
                    theme: 'plain',
                    margin: tableMargin,
                    styles: { fontSize: 9 }
                });

                // === GLOBAL PAGE FOOTER ===
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);

                    // Draw line and footer content precisely where the autoTable margin left off
                    doc.setDrawColor(180, 180, 180);
                    doc.setLineWidth(0.5);
                    doc.line(40, 775, pageWidth - 40, 775);

                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(0, 0, 0);
                    doc.text('Plot 1459 Block 216, Agape Close,', 40, 789);
                    doc.text('Off Ntinda Kiwatule Road', 40, 799);
                    doc.text('Kampala-Uganda', 40, 809);

                    doc.text('Tel: +256-782-207009', 212, 789);
                    doc.text('Tel: +256-393-294531', 212, 799);
                    doc.text('info@spekeugandaholidays.com', 212, 809);

                    doc.text('Speke Uganda Holidays', 384, 789);
                    doc.text('www.spekeugandaholidays.com', 384, 799);

                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Tour Itinerary Generated: ${this.getDateNow()}`, 40, 825);
                    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, 825, { align: 'right' });
                }

                return doc.output("bloburl");

            } catch (error) {
                console.error("PDF Generation Error:", error);
                throw error;
            }
        },

        /**
         * Generate Booking Form PDF Report (Landscape A4)
         * @param {Array} tourItineraries - Maps to Tour Activities
         * @param {Object} selectedTour - Booking Header Summary
         * @param {Array} tourGuests - Guests Table
         * @param {Array} tourLodges - Accommodation Table
         * @param {Array} tourBoats - Boats Table
         * @param {Array} tourFlights - Flights Table
         * @param {Array} tourPermits - Permits Table
         * @param {String} logoImage - Base64 PNG logo
         */
        async _generateBookingForm(tourItineraries, selectedTour, tourGuests, tourLodges, tourBoats, tourFlights, tourPermits, logoImage) {
            try {
                // Initialize doc in landscape orientation
                const doc = new jsPDF("landscape", "pt", "a4");
                const pageWidth = doc.internal.pageSize.width;
                let finalY = 135; // Starting Y coordinate after the header

                // === DOCUMENT HEADER ===
                this.addReportHeader(doc, logoImage);

                // Define a shared style for section headers
                const sectionHeaderStyle = {
                    halign: 'left',
                    fontStyle: 'bold',
                    fontSize: 11,
                    textColor: STYLES.COLORS.HEADER_TEXT, // Speke Green
                };

                // === 1. BOOKING SUMMARY SECTION (selectedTour) ===
                const summaryBody = [
                    [
                        { content: 'REF NO:', styles: { fontStyle: 'bold' } },
                        selectedTour?.reference_number || '',
                        { content: 'Guide Language:', styles: { fontStyle: 'bold' } },
                        selectedTour?.guide_language || ''
                    ],
                    [
                        { content: 'BOOKING NAME:', styles: { fontStyle: 'bold' } },
                        selectedTour?.tour_title || '',
                        { content: 'Dietary requirements:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'AGENT:', styles: { fontStyle: 'bold' } },
                        selectedTour?.agent?.name || '',
                        { content: 'Permit Status:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'Tour Starts:', styles: { fontStyle: 'bold' } },
                        selectedTour?.from || '',
                        { content: 'Per Person:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'Tour Ends:', styles: { fontStyle: 'bold' } },
                        selectedTour?.to || '',
                        { content: 'Single Room Surcharge:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ]
                ];

                doc.autoTable({
                    startY: finalY,
                    body: summaryBody,
                    theme: 'grid',
                    styles: { fontSize: 10, cellPadding: 5, textColor: [0, 0, 0] },
                    columnStyles: {
                        0: { fillColor: [245, 245, 245], cellWidth: 120 }, // Light gray background for labels
                        1: { cellWidth: 260 },
                        2: { fillColor: [245, 245, 245], cellWidth: 140 },
                        3: { cellWidth: 260 }
                    },
                    margin: { left: 40, right: 40 }
                });

                // === 2. GUESTS SECTION ===
                finalY = doc.lastAutoTable.finalY + 30; // Added 30pt gap

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'GUESTS', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                finalY = doc.lastAutoTable.finalY + 20;

                const guestHead = [['Name of Guest', 'Date of Birth', 'Passport Details', 'Arrival', 'Departure']];
                const guestBody = (tourGuests && tourGuests.length > 0)
                    ? tourGuests.map(item => [
                        item.name || '',
                        item.dob || '',
                        item.passport_no || '',
                        item.arrival_info || '',
                        item.departure_info || ''
                    ])
                    : [['No guest information available.', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: guestHead,
                    body: guestBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: { left: 40, right: 40 },
                });

                // === 3. ACCOMMODATION SECTION ===
                finalY = doc.lastAutoTable.finalY + 30; // Added 30pt gap

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'ACCOMMODATION', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                finalY = doc.lastAutoTable.finalY + 20;

                const accHead = [['No. Nights', 'Name of Accommodation', 'Room', 'Rooms', 'Meal Plan', 'Check In', 'Check Out', 'Booking Date', 'Confirm Date', 'Comments']];
                const accBody = (tourLodges && tourLodges.length > 0)
                    ? tourLodges.map(item => [
                        item.no_nights || '',
                        item.lodge?.name || '',
                        item.room?.name || '',
                        item.room_no || '',
                        item.meal?.name || '',
                        item.check_in || '',
                        item.check_out || '',
                        item.booking_date || '',
                        item.confirmation_date || '',
                        '' // Comments left blank
                    ])
                    : [['No accommodation information available.', '', '', '', '', '', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: accHead,
                    body: accBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: { left: 40, right: 40 },
                });

                // === PAGE BREAK BEFORE ACTIVITIES ===
                doc.addPage();
                this.addReportHeader(doc, logoImage);
                finalY = 135; // Reset starting Y for the new page

                // === 4. TOUR ACTIVITIES SECTION ===
                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'TOUR ACTIVITIES', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                finalY = doc.lastAutoTable.finalY + 20;

                const activityHead = [['Number of Pax', 'Activity', 'Location', 'Date']];
                const activityBody = (tourItineraries && tourItineraries.length > 0)
                    ? tourItineraries.map(item => [
                        item.pax || item.itinerary_pax || selectedTour?.pax || '',
                        item.itinerary_title || item.item?.title || '',
                        item.park?.name || item.destination || '',
                        item.date || ''
                    ])
                    : [['No activities available.', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: activityHead,
                    body: activityBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: { left: 40, right: 40 }
                });

                // === 5. TOUR BOATS SECTION ===
                finalY = doc.lastAutoTable.finalY + 30; // Added 30pt gap

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'TOUR BOATS', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                finalY = doc.lastAutoTable.finalY + 20;

                const boatHead = [['Number of Pax', 'Name of Boat Co', 'Date of Activity', 'Booking Date', 'Confirmation Date']];
                const boatBody = (tourBoats && tourBoats.length > 0)
                    ? tourBoats.map(item => [
                        item.pax || selectedTour?.pax || '', // Fallback to total pax if specific boat pax is missing
                        item.boat?.name || '',
                        item.date || '',
                        item.booking_date || '',
                        item.confirmation_date || ''
                    ])
                    : [['No boat reservations available.', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: boatHead,
                    body: boatBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: { left: 40, right: 40 },
                });

                // === GLOBAL PAGE FOOTER (Landscape Adjusted) ===
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);

                    // Top rule line for footer (Landscape A4 Height is 595.28, adjust anchor to 535)
                    doc.setDrawColor(180, 180, 180);
                    doc.setLineWidth(0.5);
                    doc.line(40, 535, pageWidth - 40, 535);

                    // Left column: address
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(0, 0, 0);
                    doc.text('Plot 1459 Block 216, Agape Close,', 40, 549);
                    doc.text('Off Ntinda Kiwatule Road', 40, 559);
                    doc.text('Kampala-Uganda', 40, 569);

                    // Center column: phone + email (centered relative to landscape width)
                    doc.text('Tel: +256-782-207009', pageWidth / 2 - 50, 549);
                    doc.text('Tel: +256-393-294531', pageWidth / 2 - 50, 559);
                    doc.text('info@spekeugandaholidays.com', pageWidth / 2 - 50, 569);

                    // Right column: company + website
                    doc.text('Speke Uganda Holidays', pageWidth - 160, 549);
                    doc.text('www.spekeugandaholidays.com', pageWidth - 160, 559);

                    // Page number & Date
                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Booking Form Generated: ${this.getDateNow()}`, 40, 580);
                    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, 580, { align: 'right' });
                }

                return doc.output("bloburl");

            } catch (error) {
                console.error("Booking Form Generation Error:", error);
                throw error;
            }
        },

        async generateBookingForm(tourItineraries, selectedTour, tourGuests, tourLodges, tourBoats, tourFlights, tourPermits, logoImage) {
            try {
                // Initialize doc in landscape orientation
                const doc = new jsPDF("landscape", "pt", "a4");
                const pageWidth = doc.internal.pageSize.width;
                let finalY = 135; // Starting Y coordinate after the header

                // Define a shared style for section headers
                const sectionHeaderStyle = {
                    halign: 'left',
                    fontStyle: 'bold',
                    fontSize: 11,
                    textColor: STYLES.COLORS.HEADER_TEXT, // Speke Green
                };

                // Shared Page Margins for autoTable. 
                // Landscape A4 height is ~595. bottom: 80 protects the footer at Y:535.
                // top: 120 ensures auto-generated pages leave room for the header.
                const tableMargin = { left: 40, right: 40, top: 120, bottom: 80 };

                // === 1. BOOKING SUMMARY SECTION (selectedTour) ===
                const summaryBody = [
                    [
                        { content: 'REF NO:', styles: { fontStyle: 'bold' } },
                        selectedTour?.reference_number || '',
                        { content: 'Guide Language:', styles: { fontStyle: 'bold' } },
                        selectedTour?.guide_language || ''
                    ],
                    [
                        { content: 'BOOKING NAME:', styles: { fontStyle: 'bold' } },
                        selectedTour?.tour_title || '',
                        { content: 'Dietary requirements:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'AGENT:', styles: { fontStyle: 'bold' } },
                        selectedTour?.agent?.name || '',
                        { content: 'Permit Status:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'Tour Starts:', styles: { fontStyle: 'bold' } },
                        selectedTour?.from || '',
                        { content: 'Per Person:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ],
                    [
                        { content: 'Tour Ends:', styles: { fontStyle: 'bold' } },
                        selectedTour?.to || '',
                        { content: 'Single Room Surcharge:', styles: { fontStyle: 'bold' } },
                        '' // Placeholder
                    ]
                ];

                doc.autoTable({
                    startY: finalY,
                    body: summaryBody,
                    theme: 'grid',
                    styles: { fontSize: 10, cellPadding: 5, textColor: [0, 0, 0] },
                    columnStyles: {
                        0: { fillColor: [245, 245, 245], cellWidth: 120 },
                        1: { cellWidth: 260 },
                        2: { fillColor: [245, 245, 245], cellWidth: 140 },
                        3: { cellWidth: 260 }
                    },
                    margin: tableMargin
                });

                // === 2. GUESTS SECTION ===
                finalY = doc.lastAutoTable.finalY + 30;

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'GUESTS', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                finalY = doc.lastAutoTable.finalY + 10;

                const guestHead = [['Name of Guest', 'Date of Birth', 'Passport Details', 'Arrival', 'Departure']];
                const guestBody = (tourGuests && tourGuests.length > 0)
                    ? tourGuests.map(item => [
                        item.name || '',
                        item.dob || '',
                        item.passport_no || '',
                        item.arrival_info || '',
                        item.departure_info || ''
                    ])
                    : [['No guest information available.', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: guestHead,
                    body: guestBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: tableMargin,
                });

                // === 3. ACCOMMODATION SECTION ===
                finalY = doc.lastAutoTable.finalY + 30;

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'ACCOMMODATION', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                finalY = doc.lastAutoTable.finalY + 10;

                const accHead = [['No. Nights', 'Name of Accommodation', 'Room', 'Rooms', 'Meal Plan', 'Check In', 'Check Out', 'Booking Date', 'Confirm Date', 'Comments']];
                const accBody = (tourLodges && tourLodges.length > 0)
                    ? tourLodges.map(item => [
                        item.no_nights || '',
                        item.lodge?.name || '',
                        item.room?.name || '',
                        item.room_no || '',
                        item.meal?.name || '',
                        item.check_in || '',
                        item.check_out || '',
                        item.booking_date || '',
                        item.confirmation_date || '',
                        '' // Comments left blank
                    ])
                    : [['No accommodation information available.', '', '', '', '', '', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: accHead,
                    body: accBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: tableMargin,
                });

                // === 4. TOUR ACTIVITIES SECTION ===
                finalY = doc.lastAutoTable.finalY + 30;

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'TOUR ACTIVITIES', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                finalY = doc.lastAutoTable.finalY + 10;

                const activityHead = [['Number of Pax', 'Activity', 'Location', 'Date']];
                const activityBody = (tourItineraries && tourItineraries.length > 0)
                    ? tourItineraries.map(item => [
                        item.pax || item.itinerary_pax || selectedTour?.pax || '',
                        item.itinerary_title || item.item?.title || '',
                        item.park?.name || item.destination || '',
                        item.date || ''
                    ])
                    : [['No activities available.', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: activityHead,
                    body: activityBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: tableMargin
                });

                // === 5. TOUR BOATS SECTION ===
                finalY = doc.lastAutoTable.finalY + 30;

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'TOUR BOATS', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: tableMargin,
                });

                finalY = doc.lastAutoTable.finalY + 10;

                const boatHead = [['Number of Pax', 'Name of Boat Co', 'Date of Activity', 'Booking Date', 'Confirmation Date']];
                const boatBody = (tourBoats && tourBoats.length > 0)
                    ? tourBoats.map(item => [
                        item.pax || selectedTour?.pax || '',
                        item.boat?.name || '',
                        item.date || '',
                        item.booking_date || '',
                        item.confirmation_date || ''
                    ])
                    : [['No boat reservations available.', '', '', '', '']];

                doc.autoTable({
                    startY: finalY,
                    head: boatHead,
                    body: boatBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        fontStyle: 'bold',
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, cellPadding: 4, textColor: [0, 0, 0] },
                    margin: tableMargin,
                });

                // === GLOBAL PAGE HEADER & FOOTER LOOPER ===
                // This loops through every single page created (manually or by autoTable) 
                // and stamps the header and footer on them.
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);

                    // --- DRAW HEADER ON EVERY PAGE ---
                    this.addReportHeader(doc, logoImage);

                    // --- DRAW FOOTER ON EVERY PAGE ---
                    // Top rule line for footer (Landscape A4 Height is 595.28, adjust anchor to 535)
                    doc.setDrawColor(180, 180, 180);
                    doc.setLineWidth(0.5);
                    doc.line(40, 535, pageWidth - 40, 535);

                    // Left column: address
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(0, 0, 0);
                    doc.text('Plot 1459 Block 216, Agape Close,', 40, 549);
                    doc.text('Off Ntinda Kiwatule Road', 40, 559);
                    doc.text('Kampala-Uganda', 40, 569);

                    // Center column: phone + email (centered relative to landscape width)
                    doc.text('Tel: +256-782-207009', pageWidth / 2 - 50, 549);
                    doc.text('Tel: +256-393-294531', pageWidth / 2 - 50, 559);
                    doc.text('info@spekeugandaholidays.com', pageWidth / 2 - 50, 569);

                    // Right column: company + website
                    doc.text('Speke Uganda Holidays', pageWidth - 160, 549);
                    doc.text('www.spekeugandaholidays.com', pageWidth - 160, 559);

                    // Page number & Date
                    doc.setFontSize(7);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Booking Form Generated: ${this.getDateNow()}`, 40, 580);
                    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, 580, { align: 'right' });
                }

                return doc.output("bloburl");

            } catch (error) {
                console.error("Booking Form Generation Error:", error);
                throw error;
            }
        },

        sectionHeading(doc, title, startY = null) {
            doc.autoTable({
                startY: startY,
                body: [[{
                    content: title,
                    styles: { ...STYLES.FONTS.HEADING, cellPadding: { top: 10, bottom: 5 } }
                }]],
                theme: 'plain',
                margin: { left: 35 },
            });
        },

        // Standard Report Header (Logo and Contact Info)
        addReportHeader(doc, logoImage) {
            const pageWidth = doc.internal.pageSize.width; // A4 is usually 595.28 pt
            const centerX = pageWidth / 2;

            // 1. Center the Logo[cite: 1]
            if (logoImage) {
                const imgWidth = 180; // Standardize width for centered look
                const imgHeight = 60;
                const imgX = centerX - (imgWidth / 2); // Calculate start point for centering
                doc.addImage(logoImage, 'PNG', imgX, 15, imgWidth, imgHeight);
            }

            // 2. Center the Text on the next line
            doc.setFontSize(9);
            doc.setTextColor(80, 80, 80); // Professional gray
            doc.setFont("helvetica", "normal");

            const addressLine = "Speke Uganda Holidays P. O. Box 23532 Plot 47, Ntinda Road Kampala Uganda";

            // Setting align to 'center' ensures the text pivots on the centerX coordinate
            doc.text(addressLine, centerX, 85, { align: 'center' });

            // 3. Decorative Line below the header[cite: 1]
            doc.setDrawColor(200, 200, 200);
            doc.line(40, 95, pageWidth - 40, 95);
        },


        /**
         * Generate Tour Invoice PDF Report (Portrait A4)
         * @param {Object} selectedInvoice - Invoice Header & Summary Data
         * @param {Object} selectedTour - Tour Summary Data
         * @param {Array} invoiceSchedule - Payment Plan Data
         * @param {Array} invoicePayables - Invoice Payables Data
         * @param {Array} invoicePayments - (Reserved for future/additional payment logic)
         * @param {String} logoImage - Base64 PNG logo
         */
        async generateTourInvoice(selectedInvoice, selectedTour, invoiceSchedule, invoicePayables, invoicePayments, logoImage) {
            try {
                // Initialize doc in portrait orientation
                const doc = new jsPDF("portrait", "pt", "a4");
                const pageWidth = doc.internal.pageSize.width;
                let finalY = 40;

                // Shared Style for Section Headers
                const sectionHeaderStyle = {
                    halign: 'left',
                    fontStyle: 'bold',
                    fontSize: 11,
                    textColor: STYLES.COLORS.HEADER_TEXT, // Maps to [0, 0, 0]
                };

                // Currency Formatter Helper
                const formatCurrency = (amount) => {
                    const num = parseFloat(amount || 0);
                    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                };

                // === GLOBAL HEADER ===
                doc.autoTable({
                    startY: finalY,
                    body: [[{
                        content: 'TOUR INVOICE',
                        styles: {
                            halign: 'left',
                            ...STYLES.FONTS.TITLE, // Brings in fontSize 16 and fontStyle bold
                            textColor: STYLES.COLORS.HEADER_TEXT
                        }
                    }]],
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });
                finalY = doc.lastAutoTable.finalY + 10;

                // === SECTION 1: INVOICE SUMMARY GRID (3 Columns x 10 Rows) ===
                const spekeAddressInfo = "Speke Uganda Holidays\nP. O. Box 23532 Kampala, Uganda\nPlot 1459 Block 216, Agape Close,\nOff Ntinda Kiwatule Road\nTel +256 782 207009, +256 393 294531\ninfo@spekeugandaholidays.com\nwww.spekeugandaholidays.com";

                const summaryBody = [
                    // Row 1
                    [{ content: 'Invoice No:', styles: { fontStyle: 'bold' } }, selectedInvoice?.reference || '', { content: '', rowSpan: 5, styles: { minCellHeight: 60 } }],
                    // Row 2
                    [{ content: 'Client:', styles: { fontStyle: 'bold' } }, selectedInvoice?.client_ref || ''],
                    // Row 3
                    [{ content: 'Bill To:', styles: { fontStyle: 'bold' } }, selectedInvoice?.bill_to || ''],
                    // Row 4
                    [{ content: 'Address:', styles: { fontStyle: 'bold' } }, selectedInvoice?.address || ''],
                    // Row 5
                    [{ content: 'Date:', styles: { fontStyle: 'bold' } }, selectedInvoice?.invoice_date || ''],

                    // Row 6
                    [{ content: 'E-Mail:', styles: { fontStyle: 'bold' } }, selectedInvoice?.agent?.email || '', { content: spekeAddressInfo, rowSpan: 5, styles: { halign: 'right', valign: 'top', cellPadding: 5 } }],
                    // Row 7
                    [{ content: 'Deposit Received:', styles: { fontStyle: 'bold' } }, formatCurrency(selectedInvoice?.deposit_received)],
                    // Row 8
                    [{ content: 'Invoice Total:', styles: { fontStyle: 'bold' } }, formatCurrency(selectedInvoice?.invoice_total)],
                    // Row 9
                    [{ content: 'Amount Due:', styles: { fontStyle: 'bold' } }, formatCurrency(selectedInvoice?.amount_due)],
                    // Row 10
                    [{ content: 'Amount Paid:', styles: { fontStyle: 'bold' } }, formatCurrency(selectedInvoice?.amount_paid)]
                ];

                doc.autoTable({
                    startY: finalY,
                    body: summaryBody,
                    theme: 'plain',
                    styles: { ...STYLES.FONTS.BODY, textColor: STYLES.COLORS.HEADER_TEXT, cellPadding: 2 },
                    columnStyles: {
                        0: { cellWidth: 100 }, // Labels
                        1: { cellWidth: 150 }, // Values
                        2: { cellWidth: pageWidth - 330, halign: 'right' } // Logo & Address (Span)
                    },
                    margin: { left: 40, right: 40 },
                    didDrawCell: (data) => {
                        // Draw the Logo in the first merged cell (Col 3, Row 1)
                        if (data.column.index === 2 && data.row.index === 0 && logoImage) {
                            const imgWidth = 140;
                            const imgHeight = 45;
                            // Right-align the image within the cell
                            const imgX = data.cell.x + data.cell.width - imgWidth - 5;
                            const imgY = data.cell.y + 5;
                            doc.addImage(logoImage, 'PNG', imgX, imgY, imgWidth, imgHeight);
                        }
                    }
                });
                finalY = doc.lastAutoTable.finalY + 20;

                // === SECTION 2: TOUR DETAILS (50% Width Left Aligned) ===
                const tourDetailsBody = [
                    [{ content: 'Destination', styles: { fontStyle: 'bold', fillColor: STYLES.COLORS.HEADER_BG } }, selectedTour?.tour_title || ''],
                    [{ content: 'Travel Dates', styles: { fontStyle: 'bold', fillColor: STYLES.COLORS.HEADER_BG } }, `${selectedTour?.from || ''} - ${selectedTour?.to || ''}`],
                    [{ content: 'No. of Travelers', styles: { fontStyle: 'bold', fillColor: STYLES.COLORS.HEADER_BG } }, selectedTour?.pax || '0']
                ];

                doc.autoTable({
                    startY: finalY,
                    body: tourDetailsBody,
                    theme: 'grid',
                    styles: { fontSize: 9, textColor: STYLES.COLORS.HEADER_TEXT, cellPadding: 4, lineColor: STYLES.COLORS.BORDER },
                    tableWidth: 250, // Approx 50% width
                    margin: { left: 40 },
                });
                finalY = doc.lastAutoTable.finalY + 30;

                // === SECTION 3: INVOICE PAYABLES ===
                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'INVOICE PAYABLES', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                const payablesHead = [['Service', 'Description', 'Amount per Traveler', 'Total Amount']];
                let payablesSubTotal = 0;

                const payablesBody = (invoicePayables && invoicePayables.length > 0)
                    ? invoicePayables.map(item => {
                        const total = parseFloat(item.total || 0);
                        payablesSubTotal += total;
                        return [
                            item.service || '',
                            `${item.description || ''}`,
                            formatCurrency(item.unit_cost),
                            formatCurrency(total)
                        ];
                    })
                    : [['No payable services listed.', '', '', '']];

                // Add Subtotal and Total rows
                payablesBody.push(['', '', { content: 'Subtotal', styles: { fontStyle: 'bold', halign: 'right' } }, { content: formatCurrency(payablesSubTotal), styles: { fontStyle: 'bold' } }]);
                payablesBody.push(['', '', { content: 'Total all', styles: { fontStyle: 'bold', halign: 'right' } }, { content: formatCurrency(payablesSubTotal), styles: { fontStyle: 'bold' } }]);

                doc.autoTable({
                    startY: doc.lastAutoTable.finalY,
                    head: payablesHead,
                    body: payablesBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, textColor: STYLES.COLORS.HEADER_TEXT },
                    margin: { left: 40, right: 40 }
                });
                finalY = doc.lastAutoTable.finalY + 30;

                // === SECTION 4: PAYMENT PLAN ===
                // Handle internal page overflow
                if (finalY > 600) {
                    doc.addPage();
                    finalY = 40;
                }

                doc.autoTable({
                    startY: finalY,
                    body: [[{ content: 'PAYMENT PLAN', styles: sectionHeaderStyle }]],
                    theme: 'plain',
                    margin: { left: 40, right: 40, top: 0, bottom: 0 },
                });

                const scheduleHead = [['Payment', 'Description', 'Amount PP', 'Total Amount']];
                let scheduleTotal = 0;

                const scheduleBody = (invoiceSchedule && invoiceSchedule.length > 0)
                    ? invoiceSchedule.map(item => {
                        const total = parseFloat(item.total_due || 0);
                        scheduleTotal += total;
                        return [
                            item.payt_category || '',
                            `${item.plan_desc || ''}`,
                            formatCurrency(item.unit_cost),
                            formatCurrency(total)
                        ];
                    })
                    : [['No payment schedule available.', '', '', '']];

                // Add Blank row, then Total row
                scheduleBody.push(['', '', '', '']);
                scheduleBody.push(['', '', { content: 'Total Scheduled', styles: { fontStyle: 'bold', halign: 'right' } }, { content: formatCurrency(scheduleTotal), styles: { fontStyle: 'bold' } }]);

                doc.autoTable({
                    startY: doc.lastAutoTable.finalY,
                    head: scheduleHead,
                    body: scheduleBody,
                    theme: 'grid',
                    headStyles: {
                        fillColor: STYLES.COLORS.HEADER_BG,
                        textColor: STYLES.COLORS.HEADER_TEXT,
                        lineColor: STYLES.COLORS.BORDER,
                        lineWidth: 0.5
                    },
                    styles: { fontSize: 9, textColor: STYLES.COLORS.HEADER_TEXT },
                    margin: { left: 40, right: 40 }
                });
                finalY = doc.lastAutoTable.finalY + 30;

                // === SECTION 5: SPEKE INFORMATION & DISCLAIMERS ===
                // Handle internal page overflow
                if (finalY > 650) {
                    doc.addPage();
                    finalY = 40;
                }

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                doc.text("Please note that we accept credit card payments with a 4% surcharge.", 40, finalY);
                finalY += 15;
                doc.setFont('helvetica', 'bold');
                doc.text("Thank you for choosing Speke Uganda Holidays", 40, finalY);
                finalY += 20;

                const bankBody = [
                    [{ content: 'ACCOUNT NAME:', styles: { fontStyle: 'bold' } }, 'SPEKE UGANDA HOLIDAYS LTD.'],
                    [{ content: 'Address:', styles: { fontStyle: 'bold' } }, 'P. O. Box 23532 Kampala, Uganda\nPlot 1459 Block 216, Agape Close,\nOff Ntinda Kiwatule Road\nTel: +256 782 207009'],
                    [{ content: 'ACCOUNT NUMBER:', styles: { fontStyle: 'bold' } }, '17906602010501'],
                    [{ content: 'BANK:', styles: { fontStyle: 'bold' } }, 'I AND M BANK (UGANDA) LIMITED'],
                    [{ content: 'BANK ADDRESS:\nBANK SWIFT CODE:', styles: { fontStyle: 'bold' } }, 'P. O. Box 3072 Kampala, Uganda\nTOWN (KAMPALA), ORIENT PLAZA,\nPLOT 6/6A KAMPALA ROAD\nORINUGKA']
                ];

                doc.autoTable({
                    startY: finalY,
                    body: bankBody,
                    theme: 'grid',
                    styles: { fontSize: 8, textColor: STYLES.COLORS.HEADER_TEXT, cellPadding: 4, lineColor: STYLES.COLORS.BORDER },
                    columnStyles: {
                        0: { cellWidth: 120, fillColor: STYLES.COLORS.HEADER_BG },
                        1: { cellWidth: 395 }
                    },
                    margin: { left: 40, right: 40 }
                });

                // === GLOBAL PAGE FOOTER ===
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);
                    doc.setFontSize(8);
                    doc.setTextColor(150, 150, 150);
                    doc.text(`Invoice Generated: ${this.getDateNow()}`, 40, 820);
                    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 40, 820, { align: 'right' });
                }

                return doc.output("bloburl");

            } catch (error) {
                console.error("Invoice Generation Error:", error);
                throw error;
            }
        }
    }
}


export default reportHelper;

